import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import shouldShowTutorial from "../utils/shouldShowTutorial";

const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const storedAccessToken = localStorage.getItem("access");
  const [user, setUser] = useState(() => {
    return storedAccessToken ? jwtDecode(storedAccessToken) : null;
  });
  const [shouldShowTutorialBool, setShouldShowTutorialBool] = useState(false);
  const [isZeroPhaseTutorialOpen, setIsZeroPhaseTutorialOpen] = useState(false);

  const navigate = useNavigate();
  const env = process.env.REACT_APP_ENV || "development";

  const baseURLMapping = {
    production: process.env.REACT_APP_BASE_URL_PRODUCTION,
    staging: process.env.REACT_APP_BASE_URL_STAGING,
    development: process.env.REACT_APP_BASE_URL_DEV, // Default to dev
  };

  const baseURL = `${baseURLMapping[env] || process.env.REACT_APP_BASE_URL_DEV}/auth`;

  const authApi = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  const login = async (email, password) => {
    try {
      const response = await authApi.post("/token/create/", {
        email,
        password,
      });

      if (response.status === 200) {
        const { access, refresh } = response.data;

        setAccessToken(access);
        setRefreshToken(refresh);
        setUser(jwtDecode(access));

        localStorage.setItem("access", access);
        localStorage.setItem("refresh", refresh);
        localStorage.setItem(
          "should_never_show_tutorial",
          jwtDecode(access).should_never_show_tutorial
        );

        return response;
      }
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const logout = () => {
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    localStorage.removeItem("should_never_show_tutorial");
    navigate("/login");
  };

  const verifyToken = async (token) => {
    try {
      const response = await authApi.post("/token/verify/", { token });
      if (response.status === 200) {
        return true;
      }
    } catch (error) {
      console.error(
        "Validação do Access token falhou:",
        error.response?.data || error.message
      );
      return false;
    }
  };

  const handleRefreshToken = async (token) => {
    try {
      const response = await authApi.post("/token/refresh/", {
        refresh: token,
      });
      if (response.status === 200) {
        const { access } = response.data;
        setAccessToken(access);
        localStorage.setItem("access", access);
        return true;
      }
      return false;
    } catch (error) {
      console.error(
        "Erro ao atualizar token:",
        error.response?.data || error.message
      );
      return false;
    }
  };

  const handleTokenVerification = async () => {
    const storedAccessToken = localStorage.getItem("access");
    const storedRefreshToken = localStorage.getItem("refresh");

    if (!storedAccessToken || !storedRefreshToken) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      setAccessToken(null);
      setRefreshToken(null);
      setUser(null);
      navigate("/login");
      return;
    }

    try {
      const isVerified = await verifyToken(storedAccessToken);

      if (isVerified) {
        setAccessToken(storedAccessToken);
        setRefreshToken(storedRefreshToken);
        setUser(jwtDecode(storedAccessToken));
        return;
      }

      const isRefreshed = await handleRefreshToken(storedRefreshToken);

      if (isRefreshed) {
        const newAccess = localStorage.getItem("access");
        setUser(jwtDecode(newAccess));
        return;
      }

      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      setAccessToken(null);
      setRefreshToken(null);
      setUser(null);
      navigate("/login");
    } catch (error) {
      console.error("Token verification failed:", error);
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      setAccessToken(null);
      setRefreshToken(null);
      setUser(null);
      navigate("/login");
    }
  };

  const postShouldNeverShowTutorial = async () => {
    try {
      const response = await authApi.post(
        "/never-show-tutorial/",
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        }
      );
      localStorage.setItem("should_never_show_tutorial", true);
      setUser((prevUser) => ({
        ...prevUser,
        should_never_show_tutorial: true,
      }));
      setShouldShowTutorialBool(false);
      return response;
    } catch (error) {
      console.error("Erro ao postar should_never_show_tutorial:", error);
    }
  };

  const refreshUser = async () => {
    const storedAccessToken = localStorage.getItem("access");
    const storedRefreshToken = localStorage.getItem("refresh");

    if (!storedAccessToken || !storedRefreshToken) {
      console.error("Tokens não encontrados ao tentar atualizar usuário");
      return;
    }

    try {
      // Primeiro tenta verificar se o token atual é válido
      const isVerified = await verifyToken(storedAccessToken);

      if (isVerified) {
        // Se o token é válido, decodifica e atualiza
        const decodedUser = jwtDecode(storedAccessToken);
        console.log("Usuário atualizado:", decodedUser);
        setUser(decodedUser);
        setAccessToken(storedAccessToken);
      } else {
        // Se não é válido, tenta fazer refresh
        console.log("Token inválido, fazendo refresh...");
        const isRefreshed = await handleRefreshToken(storedRefreshToken);

        if (isRefreshed) {
          const newAccess = localStorage.getItem("access");
          const decodedUser = jwtDecode(newAccess);
          console.log("Token atualizado, usuário:", decodedUser);
          setUser(decodedUser);
          setAccessToken(newAccess);
        } else {
          console.error("Não foi possível atualizar o token");
        }
      }
    } catch (error) {
      console.error("Erro ao atualizar dados do usuário:", error);
    }
  };

  useEffect(() => {
    if (user) {
      const localShouldNeverShow = localStorage.getItem(
        "should_never_show_tutorial"
      );
      const shouldNeverShowTutorial = localShouldNeverShow
        ? JSON.parse(localShouldNeverShow)
        : user.should_never_show_tutorial;

      const shouldShow = shouldShowTutorial({
        shouldNeverShowTutorial: shouldNeverShowTutorial,
        firstLogin: user.first_login,
        lastLogin: user.last_login,
      });

      setShouldShowTutorialBool(shouldShow);
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        user,
        accessToken,
        refreshToken,
        verifyToken,
        handleRefreshToken,
        handleTokenVerification,
        shouldShowTutorialBool,
        setShouldShowTutorialBool,
        isZeroPhaseTutorialOpen,
        setIsZeroPhaseTutorialOpen,
        postShouldNeverShowTutorial,
        refreshUser,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
